import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {uuidZodValidator} from "@vesta/global-common-typescript/validators/zodValidators";
import {z as zod} from "zod";

/**
 * Identifies the required physical additional column that stores an Event
 * Container's Organizational Unit.
 *
 * The referenced additional column is the single source of truth for this
 * semantic relationship. Valid OU template restrictions remain on the Event
 * Container template configuration.
 */
export type OttopilotOrganizationalUnitColumnConfig = {
    columnId: Uuid;
};

export const ottopilotOrganizationalUnitColumnConfigValidator: zod.ZodType<OttopilotOrganizationalUnitColumnConfig> = zod.strictObject({
    columnId: uuidZodValidator,
});

export function requireOttopilotOrganizationalUnitColumnConfig(
    configuration: OttopilotOrganizationalUnitColumnConfig | null,
): OttopilotOrganizationalUnitColumnConfig {
    if (configuration === null) {
        throw new Error("400: This operation requires an Organizational Unit column; geography is not an OU");
    }
    return configuration;
}

export function getOttopilotOrganizationalUnitValue(
    entity: {additionalColumnValues: Readonly<Record<Uuid, unknown>>},
    configuration: OttopilotOrganizationalUnitColumnConfig | null,
): Uuid {
    return uuidZodValidator.parse(entity.additionalColumnValues[requireOttopilotOrganizationalUnitColumnConfig(configuration).columnId]);
}

export function getOptionalOttopilotOrganizationalUnitValue(
    entity: {additionalColumnValues: Readonly<Record<Uuid, unknown>>},
    configuration: OttopilotOrganizationalUnitColumnConfig | null,
): Uuid | null {
    if (configuration === null) {return null;}
    const result = uuidZodValidator.safeParse(entity.additionalColumnValues[configuration.columnId]);
    return result.success === true ? result.data : null;
}
